import axios from 'axios'
import { REACT_APP_API } from '../../config'
import { ErrorAction } from 'common/redux/toasts'

/**
 * Obtiene los mensajes de una conversación de WhatsApp por ID
 * @param {string} leadId
 * @returns {Promise} Promesa con los mensajes de la conversación
 */
export function fetchMessages(leadId, markConversationAsRead) {
    return axios
        .get(`${REACT_APP_API}/whatsapp-conversations/byLead/${leadId}?markAsRead=${markConversationAsRead}`)
        .then(response => response.data)
        .catch(error => {
            console.error('Error al obtener mensajes', error)
            throw error
        })
}

/**
 * Envía un mensaje nuevo a una conversación de WhatsApp por ID
 * @param {string} leadId ID de la conversación
 * @param {string} text Texto del mensaje a enviar
 * @param {Object} template Objeto que representa la plantilla de mensaje a enviar
 * @param {File} file Archivo adjunto a enviar
 * @returns {Promise} Promesa que representa el resultado del envío
 */
export async function sendMessage(leadData, text, template, file) {
    try {
        const formData = new FormData()
        formData.append('client_id', leadData.client_id)
        formData.append('user_id', leadData.assignedUser)
        formData.append('lead_id', leadData.id)
        formData.append('car_lot_id', leadData.car_lot_id)
        formData.append('business_unit', leadData.business_unit)
        if (leadData.branch_office_id) formData.append('branch_office_id', leadData.branch_office_id)
        formData.append('recipient_id', leadData.phone)
        formData.append('text', text)
        formData.append('template', JSON.stringify(template))
        if (file) formData.append('file', file)

        const response = await axios.put(
            `${REACT_APP_API}/whatsapp-conversations/${leadData.id}/sendMessage`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' }
            }
        )

        return response.data
    } catch (error) {
        console.error('Error al enviar mensaje', error)
        throw error
    }
}

/**
 * Crea para un usuario, un registro en el historial de conversaciones
 * @param {string} conversationId ID de la conversación
 * @param {string} historyDetails Detalles a guardar en el historial
 * @returns {Promise} Devuelve la respuesta de la creación del registro en el historial
 */
export function createConversationHistory(conversationId, historyDetails) {
    const historyBody = {
        actionType: 'updateConversation',
        actionValue: historyDetails
    }
    return axios
        .post(
            `${REACT_APP_API}/whatsapp-conversations/history/${conversationId}`,
            historyBody
        )
        .then(response => response.data)
        .catch(error => {
            console.error('Error al crear historial de conversación', error)
            throw error
        })
}

/**
 * Marca una conversación de WhatsApp como leída
 * @param {string} conversationId ID de la conversación
 * @returns {Promise}
 */
export function markAsRead(conversationId) {
    const payload = {
        has_unread_messages: false
    }

    return axios
        .patch(`${REACT_APP_API}/whatsapp-conversations/${conversationId}`, payload)
        .then(response => response.data)
        .catch(error => {
            console.error('Error al marcar la conversacion como leída', error)
            throw error
        })
}

/**
 * Obtiene las plantillas de mensajes de WhatsApp
 * @returns {Promise}
 */
export async function getWhatsappTemplates(leadId) {
    try {
        const response = await axios.get(
            `${REACT_APP_API}/whatsapp-conversations/templates`,
            { params: { leadId } }
        )
        return response.data
    } catch (error) {
        console.log('Error al obtener las plantillas', error)
        throw error
    }
}

/**
 * Obtiene la plantilla a referenciar en redirecciones hacia el chat de WhatsApp
 * @param {string} leadId
 * @returns {Promise}
 */
export async function getRedirectTemplate(leadId) {
    try {
        const { data: templates } = await axios.get(
            `${REACT_APP_API}/whatsapp-conversations/templates`,
            { params: { leadId, redirect: true } }
        )
        const redirectTemplate = templates.find(t => t.templateName?.startsWith('redirect_'))
        if (!redirectTemplate) throw new Error('No se encontró plantilla de redirección')
        return redirectTemplate
    } catch {
        ErrorAction({ title: "El catálogo no se puede compartir por WhatsApp, porque no hay plantilla de envío creada." })
    }
}

export function markAudioAsPlayed(leadId, messageId) {
    const payload = {
        messageId
    }
    return axios
        .put(`${REACT_APP_API}/whatsapp-conversations/audio/${leadId}`, payload)
        .then(response => response.data)
        .catch(error => {
            console.error('Error al marcar la conversacion como leída', error)
            throw error
        })
}

export async function hasAnotherActiveConversation(leadId, companyId, phoneId) {
    return axios
        .get(`${REACT_APP_API}/whatsapp-conversations/hasAnotherActiveConversation`, {
            params: { leadId, companyId, phoneId }
        })
        .then(response => response.data.data)
        .catch(error => {
            console.error('Error al obtener otra conversación activa', error)
            throw error
        })
}